import { Onboarding } from '@/onboarding/layout/OnboardingPageTemplate'
import { useForm } from 'react-hook-form'
import { useEffect } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { mobileVerifySchema, MobileVerifyFormValues } from '../rules/mobileVerifySchema'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/common/ui/input-otp'
import { Button } from '@/common/ui'
import { RefreshCw } from 'lucide-react'
import { ONBOARDING_COPY, VERIFICATION_COPY } from '@/locales/en'
import { formatMobileForDisplay } from '@/user/mobile/MobileUtils'

export type PageProps = {
    progress?: number
    mobile?: string
    otp?: string
    resendTimer?: number
    resent?: boolean
    error?: string
    loading?: boolean
    disabled?: boolean
}

interface ViewModelProps extends PageProps {
    /** Form submission handler — *required for production use*; may be omitted in Storybook previews where only form state is displayed. */
    onSubmit?: (data: MobileVerifyFormValues) => void
    onBack?: () => void
    onOtpChange?: (otp: string) => void
    onResend?: () => void
}

export function MobileVerifyForm({
    progress = 30,
    mobile,
    otp = '',
    resendTimer = 0,
    resent = false,
    error,
    loading,
    disabled,
    onSubmit,
    onBack,
    onOtpChange,
    onResend,
}: ViewModelProps) {
    const { handleSubmit, setValue, watch } = useForm<MobileVerifyFormValues>({
        resolver: yupResolver(mobileVerifySchema),
        mode: 'onChange',
        defaultValues: { otp },
    })

    useEffect(() => {
        setValue('otp', otp)
    }, [otp, setValue])

    const currentOtp = watch('otp', otp)

    const handleOtpChange = (str: string) => {
        const cleaned = str.replace(/\D/g, '')
        setValue('otp', cleaned)
        if (onOtpChange) {
            onOtpChange(cleaned)
        }
    }

    const isDisabled = disabled ?? (currentOtp.length < 6 || loading)

    return (
        <Onboarding
            title={ONBOARDING_COPY.MobileVerification.title}
            isLoading={loading}
            description={
                <>
                    {ONBOARDING_COPY.MobileVerification.description} {formatMobileForDisplay(mobile)}
                </>
            }
            cta={ONBOARDING_COPY.next}
            onSubmit={onSubmit ? handleSubmit(onSubmit) : () => {}}
            onBack={onBack}
            disabled={isDisabled}
            progress={progress}
        >
            <div className="w-full grid place-items-center gap-6">
                <div className="self-stretch inline-flex flex-col justify-start items-center gap-2">
                    <InputOTP maxLength={6} onChange={handleOtpChange} value={currentOtp}>
                        <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                        </InputOTPGroup>
                    </InputOTP>
                    {error && (
                        <div className="text-left sm:text-center justify-start text-sentiment-bad text-sm font-normal font-['Inter'] leading-tight">
                            {error}
                        </div>
                    )}
                </div>
                <div className="self-stretch inline-flex justify-center items-center gap-6">
                    <Button
                        variant="ghost"
                        disabled={resendTimer > 0 || resent}
                        onClick={onResend}
                        className={`h-10 px-6 py-2 rounded-[100px] flex justify-center items-center gap-2 transition-all duration-200 ${resent ? 'bg-green-100 text-green-700' : ''}`}
                    >
                        <RefreshCw strokeWidth="2.5px" className={`h-5 w-5${resent ? ' animate-spin' : ''}`} />
                        <div className="justify-start text-content-primary text-sm font-medium font-['Inter'] leading-tight">
                            {resent
                                ? VERIFICATION_COPY.sent
                                : resendTimer > 0
                                  ? VERIFICATION_COPY.waiting.replace('{time}', resendTimer.toString())
                                  : VERIFICATION_COPY.resend}
                        </div>
                    </Button>
                </div>
            </div>
        </Onboarding>
    )
}
